/**
 * ExploreDestinations Component
 * 
 * Displays a tabbed grid of location links (Tours/Courses) on the homepage footer.
 * Similar to Airbnb's "Inspiration for future getaways" section.
 */

import { useState, useEffect } from 'react';
import classNames from 'classnames';

import { NamedLink } from '../../components';

import css from './ExploreDestinations.module.css';

const INITIAL_VISIBLE_COUNT = 12;

const ExploreDestinations = props => {
    const { className, rootClassName } = props;

    const [locations, setLocations] = useState([]);
    const [activeTab, setActiveTab] = useState('tours');
    const [isExpanded, setIsExpanded] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    // Fetch locations on mount
    useEffect(() => {
        const fetchLocations = async () => {
            try {
                setIsLoading(true);
                const response = await fetch('/api/locations');
                if (!response.ok) {
                    throw new Error('Failed to fetch locations');
                }
                const data = await response.json();
                setLocations(data.data || []);
            } catch (err) {
                console.error('Error fetching locations:', err);
                setError(err.message);
            } finally {
                setIsLoading(false);
            }
        };

        fetchLocations();
    }, []);

    const classes = classNames(rootClassName || css.root, className);

    // Filter locations based on active tab
    const filteredLocations = locations.filter(loc => {
        if (activeTab === 'tours') {
            return loc.tourCount > 0;
        } else if (activeTab === 'courses') {
            return loc.courseCount > 0;
        }
        return true;
    });

    // Limit visible locations unless expanded
    const visibleLocations = isExpanded
        ? filteredLocations
        : filteredLocations.slice(0, INITIAL_VISIBLE_COUNT);

    const hasMoreLocations = filteredLocations.length > INITIAL_VISIBLE_COUNT;

    if (isLoading) {
        return (
            <section className={classes}>
                <div className={css.container}>
                    <div className={css.loadingState}>
                        Loading destinations...
                    </div>
                </div>
            </section>
        );
    }

    if (error || locations.length === 0) {
        return null; // Don't render if no data
    }

    return (
        <section className={classes}>
            <div className={css.container}>
                <h2 className={css.title}>
                    Explore diving destinations
                </h2>

                {/* Tabs */}
                <div className={css.tabs}>
                    <button
                        className={classNames(css.tab, { [css.tabActive]: activeTab === 'tours' })}
                        onClick={() => {
                            setActiveTab('tours');
                            setIsExpanded(false);
                        }}
                    >
                        Tours
                    </button>
                    <button
                        className={classNames(css.tab, { [css.tabActive]: activeTab === 'courses' })}
                        onClick={() => {
                            setActiveTab('courses');
                            setIsExpanded(false);
                        }}
                    >
                        Courses
                    </button>
                </div>

                {/* Locations Grid */}
                <div className={css.grid}>
                    {visibleLocations.map(location => (
                        <NamedLink
                            key={location.slug}
                            name="LocationLandingPage"
                            params={{ location: location.slug }}
                            className={css.locationLink}
                        >
                            <span className={css.cityName}>{location.city}</span>
                            <span className={css.listingCount}>
                                {activeTab === 'tours'
                                    ? `${location.tourCount} tour${location.tourCount !== 1 ? 's' : ''}`
                                    : `${location.courseCount} course${location.courseCount !== 1 ? 's' : ''}`
                                }
                            </span>
                        </NamedLink>
                    ))}
                </div>

                {/* Show More Button */}
                {hasMoreLocations && !isExpanded && (
                    <button
                        className={css.showMoreButton}
                        onClick={() => setIsExpanded(true)}
                    >
                        Show more
                        <svg className={css.chevronIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                    </button>
                )}

                {isExpanded && hasMoreLocations && (
                    <button
                        className={css.showMoreButton}
                        onClick={() => setIsExpanded(false)}
                    >
                        Show less
                        <svg className={css.chevronIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                        </svg>
                    </button>
                )}
            </div>
        </section>
    );
};

export default ExploreDestinations;
